import React, { useState, useEffect, useLayoutEffect, useRef, useMemo, useCallback, startTransition } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  ArrowLeft,
  Settings,
  Play,
  Pause,
  SkipBack,
  SkipForward,
  Clock,
  Maximize2,
  Music,
  Eye,
  EyeOff,
} from "lucide-react";
import { SongStaffRenderer } from "../components/SongStaffRenderer";
import { SongFretboardFlow } from "../components/SongFretboardFlow";
import { SongYouTubePlayer, type SongYouTubePlayerRef } from "../components/songbook/SongYouTubePlayer";
import { useSongLibrary } from "../hooks/useSongLibrary";
import {
  TASK_SONG_ROUTE_ID,
  loadTaskSongSession,
  type TaskSongSessionPayload,
} from "../lib/taskSongBridge";
import {
  useLinkedLaneScroll,
  applyLaneScrollPositions,
} from "../hooks/useLinkedLaneScroll";
import { useLaneScrollInteraction } from "../hooks/useLaneScrollInteraction";
import { usePracticeMode } from "../contexts/PracticeModeContext";
import { useGuitarInput } from "../contexts/GuitarInputContext";
import {
  analyzeSongDetectionProfile,
  practiceAllowsMicScoring,
} from "../lib/songDetectionProfile";
import { SongInputGuidanceBanner } from "../components/song/SongInputGuidanceBanner";
import { useAudioEngine } from "../hooks/useAudioEngine";
import {
  buildHighwayTimeLayout,
  fretboardVisualDivision,
  parseMusicXmlFull,
  prepareSongForRendering,
  getNoteX,
  type PreparedSong,
  type TimedMusicXmlNote,
} from "../lib/songEngine";
import { midiToNoteName } from "../lib/guitarEngine";
import {
  DEFAULT_SONG_VIEW_HIGHWAY_TOP_PADDING_PX,
  DEFAULT_SONG_VIEW_PIXELS_PER_BEAT,
  DEFAULT_SONG_VIEW_STAFF_TOP_PADDING_PX,
  SONG_STAFF_STAVE_SCALE,
} from "../lib/songViewDefaults";
import {
  songNeedsBassStaff,
  songStaffViewportHeight,
  SONG_PLAYHEAD_BLEED_PX,
  SONG_PLAYHEAD_WIDTH_PX,
} from "../lib/songStaffLayout";
import {
  buildSongPlaybackTimeline,
  getActiveStartTime,
  getPlaybackNoteState,
  getCountInPulseMs,
  staffPixelXAtTime,
  snapLeadingRestStaffTime,
  alignPlayheadDivisionToStaffGeometryStart,
  interpolatePlayheadContentX,
} from "../lib/songPlayback";
import {
  buildPracticeNoteScores,
  scorePracticeFrame,
  summarizePracticeScores,
  type PracticeNoteScore,
  type PracticeScoringSnapshot,
} from "../lib/noteScoring";
import { appendSongStudioSession } from "../lib/bordonaProgressStorage";
import { skipLearnRestsSync } from "../lib/learnRestSkip";
import { getStarterSongById } from "../lib/starterSongs";
import {
  buildPracticePlaybackEvents,
  finalizeExpiredPracticeEvents,
  findLearnNoteIndexForHeardPitch,
  formatExpectedEventLabel,
  isLearnEventComplete,
  getEventForNoteIndex,
  isPolyphonicEvent,
  refreshEventStatuses,
  eventWindowEndMs,
  type PracticePlaybackEvent,
} from "../lib/practiceEventScoring";
import {
  learnPolyCaptureDurationMs,
  shouldTriggerLearnPolyCapture,
} from "../lib/polyEventCapture";
import {
  recordHeardPitch,
  matchPendingNoteIndicesFromHeard,
  type HeardPitchSample,
} from "../lib/practiceMonoChordAssist";
import {
  getEventDurationMs,
  getLearnDetectionProfile,
  pitchClass,
  PRACTICE_PLAYHEAD_LOOKAHEAD_MS,
  DEFAULT_PITCH_LATENCY_MS,
} from "../lib/tempoAdaptiveTiming";
import type { MonoDetectedNote } from "../hooks/useMonoPitchDetector";
import {
  layoutPlaybackPickupIntro,
  computePickupPulseCount,
  PLAYHEAD_BASE_OFFSET_PX,
  PLAYBACK_VIEWPORT_START_MARGIN_PX,
  blendedOpeningViewportMarginPx,
  layoutIdleScoreOpening,
  layoutStaffHighwayScrollForPlayback,
} from "../lib/playbackViewportLayout";
import { logPlayhead } from "../lib/playheadDebug";
import { StudioLayout } from "../components/layout/StudioLayout";
import { BeginnerPathPhaseShell } from "../components/beginnerPath/BeginnerPathPhaseShell";
import { useBeginnerPathEntry } from "../hooks/useBeginnerPathEntry";
import { beginnerPathUrl } from "../lib/beginnerPath/beginnerPathNav";
import { TaskCreatorButton } from "../components/TaskCreatorButton";
import { useBordonaAccount } from "../hooks/useBordonaAccount";
import { useStudentAuth } from "../hooks/useStudentAuth";
import { useTaskPlayer } from "../hooks/useTaskPlayer";
import { serializeSongState } from "../lib/taskEngine";
import {
  practiceScoreSummaryLines,
} from "../lib/practiceScoreDisplay";
import { PlaybackPlayheadOverlay } from "../components/PlaybackPlayheadOverlay";
import { BeatCountInOverlay } from "../components/BeatCountInOverlay";
import { SongLearnCanvasBackdrop } from "../components/SongLearnCanvasBackdrop";
import { SongPianoHighway } from "../components/song/SongPianoHighway";
import { useInstrument } from "../contexts/InstrumentContext";
import { staffGeometrySignature } from "../lib/staffGeometrySignature";

/* ─── Deep Focus Dark Theme ─── */
const C = {
  bg: "#11100f",
  bgElevated: "#1a1817",
  bgPanel: "rgba(26,24,23,0.95)",
  border: "rgba(255,255,255,0.08)",
  borderHover: "rgba(255,255,255,0.15)",
  textMain: "#e8e6e3",
  textMuted: "#9a9590",
  textDim: "#6a6560",
  accent: "#c9405a",
  accentGlow: "rgba(201,64,90,0.20)",
  accentSoft: "rgba(201,64,90,0.08)",
  danger: "#e05252",
};

/* ─── Transport Bar ─── */
const STAFF_STAVE_SCALE = SONG_STAFF_STAVE_SCALE;
const STAFF_PADDING_X = 24;
const HIGHWAY_MARGIN_LEFT = 64;

function songNoteLabel(note: TimedMusicXmlNote | null | undefined): string {
  if (!note) return "—";
  if (note.midi > 0) {
    const sound = midiToNoteName(note.midi);
    return `${sound.note}${sound.octave}`;
  }
  return `${note.step}${note.alter ? (note.alter > 0 ? "#" : "b") : ""}${note.octave}`;
}

function learnPitchMatches(heardMidi: number, expectedMidi: number, cents: number): boolean {
  const heard = Math.round(heardMidi);
  const expected = Math.round(expectedMidi);
  if (heard === expected && Math.abs(cents) <= 48) return true;
  if (heard % 12 !== expected % 12) return false;
  return Math.abs(cents) <= 52;
}

const TransportBar: React.FC<{
  isPlaying: boolean;
  progressPercent: number;
  eventIndex: number;
  totalEvents: number;
  bpm: number;
  onPlayPause: () => void;
  onStop: () => void;
  onBpmChange: (bpm: number) => void;
  currentNote?: TimedMusicXmlNote;
}> = ({ isPlaying, progressPercent, eventIndex, totalEvents, bpm, onPlayPause, onStop, onBpmChange, currentNote }) => {
  const progress = progressPercent;

  return (
    <div
      className="flex w-full max-w-[640px] items-center gap-4 px-5 py-2.5 rounded-2xl"
      style={{
        backgroundColor: C.bgPanel,
        border: `1px solid ${C.border}`,
        backdropFilter: "blur(24px)",
        WebkitBackdropFilter: "blur(24px)",
        boxShadow: "0 8px 32px rgba(0,0,0,0.5), inset 0 1px 0 rgba(255,255,255,0.04)",
      }}
    >
      {/* Info */}
      <div className="shrink-0 w-36">
        <p className="text-xs font-medium truncate" style={{ color: C.textMuted }}>
          {songNoteLabel(currentNote)}
        </p>
        <p className="text-[10px]" style={{ color: C.textDim }}>
          {eventIndex + 1} / {totalEvents}
        </p>
      </div>

      {/* Controls */}
      <div className="flex items-center gap-2">
        <button
          onClick={onStop}
          className="w-8 h-8 rounded-lg flex items-center justify-center transition-colors hover:bg-black/5"
        >
          <SkipBack className="w-4 h-4" style={{ color: C.textMuted }} />
        </button>

        <button
          onClick={onPlayPause}
          className="w-10 h-10 rounded-full flex items-center justify-center transition-all"
          style={{
            backgroundColor: isPlaying ? "rgba(0,0,0,0.08)" : C.accent,
            boxShadow: isPlaying ? "none" : `0 0 16px ${C.accentGlow}`,
          }}
        >
          {isPlaying ? (
            <Pause className="w-4 h-4" style={{ color: C.textMain }} />
          ) : (
            <Play className="w-4 h-4 text-white ml-0.5" fill="white" />
          )}
        </button>

        <button
          onClick={() => onBpmChange(Math.min(200, bpm + 5))}
          className="w-8 h-8 rounded-lg flex items-center justify-center transition-colors hover:bg-black/5"
        >
          <SkipForward className="w-4 h-4" style={{ color: C.textMuted }} />
        </button>
      </div>

      {/* BPM */}
      <div className="flex items-center gap-2 shrink-0">
        <Clock className="w-3.5 h-3.5" style={{ color: C.textDim }} />
        <input
          type="range"
          min={40}
          max={200}
          value={bpm}
          onChange={(e) => onBpmChange(Number(e.target.value))}
          className="w-20 h-1 rounded-full appearance-none cursor-pointer"
          style={{
            background: `linear-gradient(to right, ${C.accent} 0%, ${C.accent} ${((bpm - 40) / 160) * 100}%, ${C.border} ${((bpm - 40) / 160) * 100}%, ${C.border} 100%)`,
          }}
        />
        <span className="text-xs tabular-nums w-8 text-right" style={{ color: C.textMuted }}>
          {bpm}
        </span>
      </div>

      {/* Progress bar */}
      <motion.div className="flex-1 h-[3px] rounded-full relative overflow-hidden" style={{ backgroundColor: C.border }}>
        <motion.div
          className="absolute left-0 top-0 h-full rounded-full"
          style={{
            width: `${progress}%`,
            backgroundColor: C.accent,
            boxShadow: `0 0 10px ${C.accentGlow}`,
          }}
          transition={{ duration: isPlaying ? 0.05 : 0.2, ease: "linear" }}
        />
      </motion.div>
    </div>
  );
};

/* ─── Settings Drawer ─── */
const SettingsDrawer: React.FC<{
  open: boolean;
  onClose: () => void;
  showStaff: boolean;
  onShowStaffChange: (v: boolean) => void;
  showVideo: boolean;
  onShowVideoChange: (v: boolean) => void;
  inputMode: "mic" | "line";
  onInputModeChange: (mode: "mic" | "line") => void;
  deviceId: string | null;
  onDeviceIdChange: (id: string | null) => void;
  audioInputs: { deviceId: string; label: string }[];
  onRefreshDevices: () => void;
}> = ({
  open,
  onClose,
  showStaff,
  onShowStaffChange,
  showVideo,
  onShowVideoChange,
  inputMode,
  onInputModeChange,
  deviceId,
  onDeviceIdChange,
  audioInputs,
  onRefreshDevices,
}) => {
  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-40"
            style={{ backgroundColor: "rgba(0,0,0,0.4)" }}
            onClick={onClose}
          />
          <motion.div
            initial={{ x: 320 }}
            animate={{ x: 0 }}
            exit={{ x: 320 }}
            transition={{ type: "spring", damping: 25, stiffness: 200 }}
            className="fixed right-0 top-0 bottom-0 w-80 z-50 flex flex-col"
            style={{
              backgroundColor: C.bgElevated,
              borderLeft: `1px solid ${C.border}`,
            }}
          >
            <div className="flex items-center justify-between px-5 py-4 border-b" style={{ borderColor: C.border }}>
              <h3 className="text-sm font-medium" style={{ color: C.textMain }}>Ajustes</h3>
              <button onClick={onClose} className="w-8 h-8 rounded-lg flex items-center justify-center hover:bg-white/5">
                <ArrowLeft className="w-4 h-4" style={{ color: C.textMuted }} />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-5 space-y-6">
              <div className="space-y-3">
                <p
                  className="text-[11px] font-semibold uppercase tracking-wide"
                  style={{ color: C.textDim }}
                >
                  Entrada de audio
                </p>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => onInputModeChange("mic")}
                    className="flex-1 rounded-lg px-3 py-2 text-xs font-semibold"
                    style={{
                      backgroundColor: inputMode === "mic" ? C.accentSoft : "transparent",
                      color: inputMode === "mic" ? C.textMain : C.textMuted,
                      border: `1px solid ${inputMode === "mic" ? C.accent : C.border}`,
                    }}
                  >
                    Micrófono
                  </button>
                  <button
                    type="button"
                    onClick={() => onInputModeChange("line")}
                    className="flex-1 rounded-lg px-3 py-2 text-xs font-semibold"
                    style={{
                      backgroundColor: inputMode === "line" ? C.accentSoft : "transparent",
                      color: inputMode === "line" ? C.textMain : C.textMuted,
                      border: `1px solid ${inputMode === "line" ? C.accent : C.border}`,
                    }}
                  >
                    Cable / línea
                  </button>
                </div>
                <p className="text-[11px] leading-relaxed" style={{ color: C.textDim }}>
                  Con cable a la interfaz puedes practicar piezas con más voces. El micrófono sirve
                  mejor para melodía y aprender acordes nota a nota.
                </p>
                <label className="block text-xs font-medium" style={{ color: C.textMuted }}>
                  Dispositivo
                  <select
                    value={deviceId ?? ""}
                    onChange={(e) => onDeviceIdChange(e.target.value || null)}
                    onFocus={() => void onRefreshDevices()}
                    className="mt-1 w-full rounded-lg border px-2 py-2 text-sm"
                    style={{ borderColor: C.border, backgroundColor: C.bg, color: C.textMain }}
                  >
                    <option value="">Predeterminado del sistema</option>
                    {audioInputs.map((d) => (
                      <option key={d.deviceId} value={d.deviceId}>
                        {d.label}
                      </option>
                    ))}
                  </select>
                </label>
              </div>

              <div className="space-y-3">
                <ToggleRow
                  label="Mostrar partitura"
                  icon={<Music className="w-4 h-4" />}
                  value={showStaff}
                  onChange={onShowStaffChange}
                />
                <ToggleRow
                  label="Mostrar video"
                  icon={<Maximize2 className="w-4 h-4" />}
                  value={showVideo}
                  onChange={onShowVideoChange}
                />
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

const ToggleRow: React.FC<{
  label: string;
  icon: React.ReactNode;
  value: boolean;
  onChange: (v: boolean) => void;
}> = ({ label, icon, value, onChange }) => (
  <button
    onClick={() => onChange(!value)}
    className="w-full flex items-center justify-between px-3 py-2.5 rounded-lg transition-colors"
    style={{ backgroundColor: value ? C.accentSoft : "transparent" }}
  >
    <div className="flex items-center gap-2.5">
      <span style={{ color: C.textMuted }}>{icon}</span>
      <span className="text-sm" style={{ color: C.textMain }}>{label}</span>
    </div>
    <div
      className="w-9 h-5 rounded-full relative transition-colors"
      style={{ backgroundColor: value ? C.accent : C.border }}
    >
      <div
        className="absolute top-0.5 w-4 h-4 rounded-full bg-white transition-all"
        style={{ left: value ? 18 : 2 }}
      />
    </div>
  </button>
);

/* ─── Main Studio Page ─── */
export const SongStudioPage: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const songId = id || null;
  const { fromPath, phase } = useBeginnerPathEntry();
  const pathMode = fromPath && phase?.id === "first-melody";
  const pathExit = "/learn/path";
  const { isTeacher } = useBordonaAccount();
  const { profile, user } = useStudentAuth();
  const { submitResult, loadTask } = useTaskPlayer();
  const { items: songs, loading, saveSong } = useSongLibrary();
  const [taskSong, setTaskSong] = useState<TaskSongSessionPayload | null>(null);
  const starterMeta = getStarterSongById(songId ?? undefined);
  const [starterXml, setStarterXml] = useState<string | null>(null);
  const [starterLoadError, setStarterLoadError] = useState<string | null>(null);
  const [practiceCompleteSummary, setPracticeCompleteSummary] =
    useState<PracticeScoringSnapshot | null>(null);
  const [taskSubmitMessage, setTaskSubmitMessage] = useState<string | null>(null);
  const [savedTaskToLibrary, setSavedTaskToLibrary] = useState(false);
  const taskSongRef = useRef<TaskSongSessionPayload | null>(null);
  const profileRef = useRef(profile);
  taskSongRef.current = taskSong;
  profileRef.current = profile;

  /** Camino: solo Estrellita en Do mayor. */
  useEffect(() => {
    if (!pathMode) return;
    if (songId !== "starter-estrellita") {
      navigate(beginnerPathUrl("/songs/starter-estrellita", "first-melody"), { replace: true });
    }
  }, [pathMode, songId, navigate]);

  const wrapStudio = (node: React.ReactNode) => {
    if (pathMode && phase) {
      return (
        <BeginnerPathPhaseShell phase={phase}>
          <div className="overflow-hidden rounded-2xl border border-white/[0.08] bg-black/20 min-h-[min(72vh,720px)]">
            {node}
          </div>
        </BeginnerPathPhaseShell>
      );
    }
    return <StudioLayout>{node}</StudioLayout>;
  };

  useEffect(() => {
    if (!starterMeta) {
      setStarterXml(null);
      setStarterLoadError(null);
      return;
    }
    let cancelled = false;
    setStarterLoadError(null);
    fetch(starterMeta.assetPath)
      .then((r) => {
        if (!r.ok) throw new Error(`No se pudo cargar ${starterMeta.title}`);
        return r.text();
      })
      .then((xml) => {
        if (!cancelled) setStarterXml(xml);
      })
      .catch((e: unknown) => {
        if (!cancelled) {
          setStarterXml(null);
          setStarterLoadError(e instanceof Error ? e.message : "Error al cargar melodía");
        }
      });
    return () => {
      cancelled = true;
    };
  }, [starterMeta]);

  useEffect(() => {
    if (songId !== TASK_SONG_ROUTE_ID) {
      setTaskSong(null);
      setSavedTaskToLibrary(false);
      return;
    }
    setTaskSong(loadTaskSongSession());
  }, [songId]);

  useEffect(() => {
    if (songId !== TASK_SONG_ROUTE_ID || !taskSong?.taskId) return;
    void loadTask(taskSong.taskId, profile?.uid ?? user?.uid);
  }, [songId, taskSong?.taskId, profile?.uid, user?.uid, loadTask]);

  const song = useMemo(() => {
    if (starterMeta && starterXml) {
      return {
        id: starterMeta.id,
        title: starterMeta.title,
        artist: starterMeta.artist,
        musicXml: starterXml,
        tempo: starterMeta.tempo,
      };
    }
    if (songId === TASK_SONG_ROUTE_ID) {
      if (!taskSong?.musicXml?.trim()) return undefined;
      return {
        id: TASK_SONG_ROUTE_ID,
        title: taskSong.title,
        musicXml: taskSong.musicXml,
        youtubeUrl: taskSong.youtubeUrl,
        tempo: taskSong.tempo,
      };
    }
    return songs.find((s) => s.id === songId);
  }, [songId, taskSong, songs, starterMeta, starterXml]);

  const [isPlaying, setIsPlaying] = useState(false);
  const [currentNoteIndex, setCurrentNoteIndex] = useState(0);
  const [playbackMs, setPlaybackMs] = useState(0);
  const [bpm, setBpm] = useState(60);
  const pixelsPerBeat = DEFAULT_SONG_VIEW_PIXELS_PER_BEAT;
  const [showSettings, setShowSettings] = useState(false);
  const [showStaff, setShowStaff] = useState(true);
  const [showVideo, setShowVideo] = useState(true);
  const [staffTimeGeometry, setStaffTimeGeometry] = useState<Array<{ startTime: number; x: number }>>([]);

  const staffScrollRef = useRef<HTMLDivElement>(null);
  const highwayScrollRef = useRef<HTMLDivElement>(null);
  /** Diferencia highway − staff tras scroll programático; mantiene sync en scroll manual. */
  const laneScrollDeltaRef = useRef(0);
  const programmaticLaneScrollRef = useRef(false);
  const staffGeometrySigRef = useRef("");
  const [staffFirstNoteX, setStaffFirstNoteX] = useState(0);
  const [staffNotationLeadPx, setStaffNotationLeadPx] = useState<number | null>(null);
  const [staffScrollContentWidth, setStaffScrollContentWidth] = useState<number | null>(null);
  const [playheadLeftPx, setPlayheadLeftPx] = useState(PLAYHEAD_BASE_OFFSET_PX);

  const isPlayingRef = useRef(isPlaying);
  const playbackMsRef = useRef(playbackMs);
  const getLivePlaybackMs = useCallback(() => playbackMsRef.current, []);
  const playStartWallRef = useRef(0);
  const playStartMsRef = useRef(0);
  const currentNoteIndexRef = useRef(currentNoteIndex);
  const preparedSongRef = useRef<PreparedSong | null>(null);
  const pickupPreRollMsRef = useRef(0);
  const learnPreRollMsRef = useRef(0);
  const totalPreRollMsRef = useRef(0);

  const playbackUiFlushRef = useRef(0);
  const playbackLaneScrollFlushRef = useRef(0);

  useEffect(() => { isPlayingRef.current = isPlaying; }, [isPlaying]);
  useEffect(() => { playbackMsRef.current = playbackMs; }, [playbackMs]);
  useEffect(() => { currentNoteIndexRef.current = currentNoteIndex; }, [currentNoteIndex]);

  const displayXml = song?.musicXml || "";

  const [preparedSong, setPreparedSong] = useState<PreparedSong | null>(null);
  const [songParsePending, setSongParsePending] = useState(false);
  /** Monta mástil/piano tras el primer paint para no competir con VexFlow. */
  const [heavySurfaceReady, setHeavySurfaceReady] = useState(false);

  useEffect(() => {
    staffGeometrySigRef.current = "";
    setStaffTimeGeometry([]);
    setPreparedSong(null);
    setHeavySurfaceReady(false);
    setStaffNotationLeadPx(null);
    setStaffScrollContentWidth(null);

    if (!displayXml?.trim()) {
      setSongParsePending(false);
      return;
    }

    let cancelled = false;
    setSongParsePending(true);

    const parseSong = () => {
      try {
        const parsed = parseMusicXmlFull(displayXml);
        if (cancelled) return;
        if (parsed.notes.length === 0) {
          setPreparedSong(null);
          setSongParsePending(false);
          return;
        }
        const prepared = prepareSongForRendering(parsed.notes, parsed, { pixelsPerBeat });
        if (cancelled) return;
        if (parsed.tempo) setBpm(Math.round(parsed.tempo));
        startTransition(() => {
          setPreparedSong(prepared);
          setSongParsePending(false);
        });
      } catch {
        if (!cancelled) {
          setPreparedSong(null);
          setSongParsePending(false);
        }
      }
    };

    let idleHandle: number | ReturnType<typeof setTimeout>;
    if (typeof requestIdleCallback !== "undefined") {
      idleHandle = requestIdleCallback(parseSong, { timeout: 300 });
    } else {
      idleHandle = window.setTimeout(parseSong, 0);
    }

    return () => {
      cancelled = true;
      if (typeof cancelIdleCallback !== "undefined" && typeof idleHandle === "number") {
        cancelIdleCallback(idleHandle);
      } else {
        clearTimeout(idleHandle as ReturnType<typeof setTimeout>);
      }
    };
  }, [displayXml, pixelsPerBeat, songId]);

  useEffect(() => {
    if (!preparedSong) {
      setHeavySurfaceReady(false);
      return;
    }
    let cancelled = false;
    let raf2 = 0;
    const raf1 = requestAnimationFrame(() => {
      raf2 = requestAnimationFrame(() => {
        if (!cancelled) setHeavySurfaceReady(true);
      });
    });
    return () => {
      cancelled = true;
      cancelAnimationFrame(raf1);
      cancelAnimationFrame(raf2);
    };
  }, [preparedSong]);

  const programmaticLaneScrollUntilRef = useRef(0);

  const setLaneScrollPositions = useCallback(
    (staffEl: HTMLDivElement | null, highwayEl: HTMLDivElement | null, staffLeft: number, highwayLeft: number) => {
      applyLaneScrollPositions(
        staffEl,
        highwayEl,
        staffLeft,
        highwayLeft,
        laneScrollDeltaRef,
        programmaticLaneScrollRef,
        programmaticLaneScrollUntilRef
      );
    },
    []
  );

  const linkedScrollAttachKey = heavySurfaceReady
    ? `${preparedSong?.notes.length ?? 0}:${staffScrollContentWidth ?? 0}`
    : 0;

  useLinkedLaneScroll(
    staffScrollRef,
    highwayScrollRef,
    laneScrollDeltaRef,
    programmaticLaneScrollRef,
    linkedScrollAttachKey,
    programmaticLaneScrollUntilRef
  );

  useLaneScrollInteraction(
    staffScrollRef,
    highwayScrollRef,
    programmaticLaneScrollRef,
    programmaticLaneScrollUntilRef,
    linkedScrollAttachKey
  );

  const handleStaffTimeGeometry = useCallback(
    (geom: Array<{ startTime: number; x: number }>) => {
      const sig = staffGeometrySignature(geom);
      if (sig === staffGeometrySigRef.current) return;
      staffGeometrySigRef.current = sig;
      setStaffTimeGeometry(geom);
    },
    []
  );

  const handleStaffLayoutMeta = useCallback(
    (meta: { firstMeasureContentLeftPx: number; totalScrollWidthPx: number }) => {
      setStaffNotationLeadPx(meta.firstMeasureContentLeftPx);
      setStaffScrollContentWidth(meta.totalScrollWidthPx);
    },
    []
  );

  preparedSongRef.current = preparedSong;

  const playbackContentLayout = useMemo(() => {
    if (!preparedSong?.measures.length) return null;
    return buildHighwayTimeLayout(
      preparedSong.measures,
      preparedSong.divisions,
      pixelsPerBeat
    );
  }, [preparedSong?.measures, preparedSong?.divisions, pixelsPerBeat]);

  const playbackNotes = preparedSong?.notes ?? [];
  const currentNote = playbackNotes[currentNoteIndex];

  const songDetectionProfile = useMemo(
    () => analyzeSongDetectionProfile(playbackNotes),
    [playbackNotes]
  );

  const { surface } = useInstrument();
  const needsBassStaff = useMemo(
    () => songNeedsBassStaff(playbackNotes, surface),
    [playbackNotes, surface]
  );
  const staffViewportHeightPx = useMemo(
    () => songStaffViewportHeight(needsBassStaff),
    [needsBassStaff]
  );
  const { mode, setMode } = usePracticeMode();
  const { inputMode, setInputMode, deviceId, setDeviceId, audioInputs, refreshAudioInputs } =
    useGuitarInput();
  const modeRef = useRef(mode);
  useEffect(() => {
    modeRef.current = mode;
  }, [mode]);

  const ytPlayerRef = useRef<SongYouTubePlayerRef | null>(null);
  const youtubeUrlRef = useRef<string | undefined>(undefined);
  useEffect(() => {
    youtubeUrlRef.current = song?.youtubeUrl ?? undefined;
  }, [song?.youtubeUrl]);
  const youtubeOffsetRef = useRef(0);
  const ytFrozenAtPickupRef = useRef(false);
  const suppressYtPauseEventRef = useRef(false);

  const currentNoteRef = useRef(currentNote);
  useEffect(() => {
    currentNoteRef.current = currentNote;
  }, [currentNote]);
  const notesRef = useRef(playbackNotes);
  useEffect(() => {
    notesRef.current = playbackNotes;
  }, [playbackNotes]);

  /** Inicio de la pieza (compás 1): ancla común de scroll pentagrama / mástil. */
  const scrollAnchorStartTime = useMemo(
    () => preparedSong?.measures[0]?.startTime ?? 0,
    [preparedSong?.measures]
  );

  const playbackTimeline = useMemo(() => {
    if (!preparedSong || playbackNotes.length === 0) return null;
    return buildSongPlaybackTimeline(playbackNotes, preparedSong.divisions, bpm);
  }, [playbackNotes, preparedSong?.divisions, bpm]);

  const playbackTimelineRef = useRef(playbackTimeline);
  playbackTimelineRef.current = playbackTimeline;

  const handleYtStateChange = useCallback((ytPlaying: boolean) => {
    if (!ytPlaying && suppressYtPauseEventRef.current) {
      suppressYtPauseEventRef.current = false;
      return;
    }
    const tl = playbackTimelineRef.current;
    if (!tl || notesRef.current.length === 0) return;
    const yt = ytPlayerRef.current;
    if (!yt?.isReady()) return;
    const pr = pickupPreRollMsRef.current;
    const tpr = totalPreRollMsRef.current;
    const off = youtubeOffsetRef.current;
    const ytT = yt.getCurrentTime();
    const songMs = Math.max(0, (ytT - off) * 1000);
    const m0 = preparedSongRef.current?.measures?.[0];
    const atSongStart = songMs < 80;
    let session: number;
    let playStartMs: number;

    if (ytPlaying) {
      if (isPlayingRef.current) return;
      isPlayingRef.current = true;
      setIsPlaying(true);
      playStartWallRef.current = performance.now();
      if (modeRef.current === "practice" && atSongStart && tpr > 1e-3) {
        session = 0;
        playStartMs = m0?.isPickup && pr > 1e-3 ? -pr : 0;
        ytFrozenAtPickupRef.current = true;
        suppressYtPauseEventRef.current = true;
        yt.seekTo(off, true);
        yt.pause();
      } else {
        session = Math.min(tpr + tl.totalMs, tpr + songMs);
        playStartMs = session;
        ytFrozenAtPickupRef.current =
          modeRef.current === "practice" && tpr > 1e-3 && session < tpr - 1e-4;
        if (ytFrozenAtPickupRef.current) {
          suppressYtPauseEventRef.current = true;
          yt.seekTo(off, true);
          yt.pause();
        }
      }
      playStartMsRef.current = playStartMs;
      playbackMsRef.current = session;
      setPlaybackMs(session);
      const songT = Math.max(0, session - tpr);
      const { noteIndex } = getPlaybackNoteState(tl, songT);
      currentNoteIndexRef.current = noteIndex;
      setCurrentNoteIndex(noteIndex);
    } else {
      if (!isPlayingRef.current) return;
      isPlayingRef.current = false;
      setIsPlaying(false);
      session = Math.min(tpr + tl.totalMs, tpr + songMs);
      playbackMsRef.current = session;
      setPlaybackMs(session);
      ytFrozenAtPickupRef.current = false;
      const songT = Math.max(0, session - tpr);
      const { noteIndex } = getPlaybackNoteState(tl, songT);
      currentNoteIndexRef.current = noteIndex;
      setCurrentNoteIndex(noteIndex);
    }
  }, []);

  const highwayContentAnchorX = useMemo(() => {
    if (!playbackContentLayout || !preparedSong?.measures.length) return 0;
    const vd = fretboardVisualDivision(scrollAnchorStartTime, preparedSong.measures);
    return playbackContentLayout.contentXAtDivision(vd);
  }, [playbackContentLayout, scrollAnchorStartTime, preparedSong?.measures]);

  useEffect(() => {
    if (staffTimeGeometry.length === 0) return;
    setStaffFirstNoteX(staffPixelXAtTime(staffTimeGeometry, scrollAnchorStartTime));
  }, [staffTimeGeometry, scrollAnchorStartTime]);

  const firstAttackDiv = useMemo(() => {
    if (!preparedSong) return 0;
    const starts = preparedSong.notes.filter((n) => !n.isRest).map((n) => n.startTime);
    if (starts.length === 0) return 0;
    return Math.min(...starts);
  }, [preparedSong]);

  const pickupTotalPulses = useMemo(() => {
    if (!preparedSong?.measures[0]) return 0;
    return computePickupPulseCount(
      preparedSong.measures[0],
      preparedSong.divisions,
      preparedSong.timeSignature.beatType,
      firstAttackDiv
    );
  }, [preparedSong, firstAttackDiv]);

  const countInPulseMs = useMemo(
    () =>
      getCountInPulseMs(bpm, preparedSong?.timeSignature.beatType ?? 4),
    [bpm, preparedSong?.timeSignature.beatType]
  );

  const pickupPreRollMs = useMemo(() => {
    if (!preparedSong?.measures[0]?.isPickup || pickupTotalPulses <= 0) return 0;
    return pickupTotalPulses * countInPulseMs;
  }, [preparedSong?.measures, pickupTotalPulses, countInPulseMs]);

  useEffect(() => {
    pickupPreRollMsRef.current = pickupPreRollMs;
  }, [pickupPreRollMs]);

  const learnPreRollMs = useMemo(() => {
    if (!preparedSong?.measures[0] || preparedSong.measures[0].isPickup) return 0;
    const bt = preparedSong.timeSignature.beatType;
    const beats = preparedSong.timeSignature.beats;
    if (bt !== 4) return 0;
    if (beats !== 2 && beats !== 3 && beats !== 4) return 0;
    return beats * countInPulseMs;
  }, [preparedSong?.measures, preparedSong?.timeSignature, countInPulseMs]);

  const totalPreRollMs = pickupPreRollMs + learnPreRollMs;

  useEffect(() => {
    learnPreRollMsRef.current = learnPreRollMs;
  }, [learnPreRollMs]);
  useEffect(() => {
    totalPreRollMsRef.current = totalPreRollMs;
  }, [totalPreRollMs]);

  const activeStartTimeRaw = useMemo(() => {
    if (!playbackTimeline) return -1;
    if (pickupPreRollMs > 1e-3 && playbackMs < pickupPreRollMs - 1e-6) return -1;
    if (learnPreRollMs > 1e-3 && playbackMs >= pickupPreRollMs - 1e-6 && playbackMs < totalPreRollMs - 1e-6) return -1;
    const songMs = Math.max(0, playbackMs - totalPreRollMs);
    return getActiveStartTime(playbackTimeline, songMs);
  }, [playbackTimeline, playbackMs, pickupPreRollMs, learnPreRollMs, totalPreRollMs]);

  /** Alinea con geometría Vex (evita resaltar en clave cuando el timeline usa t=0 y el primer ataque es >0). */
  const activeStartTime = useMemo(() => {
    if (activeStartTimeRaw < 0 || staffTimeGeometry.length === 0) return activeStartTimeRaw;
    let t = snapLeadingRestStaffTime(activeStartTimeRaw, playbackNotes, scrollAnchorStartTime);
    t = alignPlayheadDivisionToStaffGeometryStart(t, scrollAnchorStartTime, staffTimeGeometry);
    return t;
  }, [activeStartTimeRaw, staffTimeGeometry, scrollAnchorStartTime, playbackNotes]);

  const sessionTotalMs = (totalPreRollMs || 0) + (playbackTimeline?.totalMs ?? 0);
  const playbackProgressPercent =
    sessionTotalMs > 1e-6 ? Math.min(100, (playbackMs / sessionTotalMs) * 100) : 0;

  const songMsForUi = Math.max(0, playbackMs - totalPreRollMs);
  const playbackEventIndex = playbackTimeline
    ? getPlaybackNoteState(playbackTimeline, songMsForUi).eventIndex
    : 0;

  const highwayOffsetX = staffFirstNoteX > 0 ? staffFirstNoteX - HIGHWAY_MARGIN_LEFT : 0;

  const applyScrollForPlaybackMs = useCallback(
    (ms: number) => {
      if (!preparedSong || !playbackTimeline) return;
      const staffContainer = staffScrollRef.current;
      const highwayContainer = highwayScrollRef.current;

      const anchorXContent = playbackContentLayout
        ? playbackContentLayout.contentXAtDivision(
            preparedSong.measures.length
              ? fretboardVisualDivision(scrollAnchorStartTime, preparedSong.measures)
              : scrollAnchorStartTime
          )
        : getNoteX(scrollAnchorStartTime, preparedSong.divisions, pixelsPerBeat);

      const anchorStaffPixelX =
        staffTimeGeometry.length > 0
          ? staffPixelXAtTime(staffTimeGeometry, scrollAnchorStartTime)
          : staffFirstNoteX;

      const xHighwayRaw = (t: number) =>
        playbackContentLayout
          ? playbackContentLayout.contentXAtDivision(
              preparedSong.measures.length
                ? fretboardVisualDivision(t, preparedSong.measures)
                : t
            )
          : getNoteX(t, preparedSong.divisions, pixelsPerBeat);

      const staffXAtTimeIntro = (t: number) =>
        staffTimeGeometry.length > 0
          ? staffPixelXAtTime(staffTimeGeometry, t)
          : anchorStaffPixelX > 0
            ? anchorStaffPixelX + xHighwayRaw(t) - anchorXContent
            : xHighwayRaw(t) * STAFF_STAVE_SCALE + STAFF_PADDING_X;

      const highwayXAtTimeIntro = (t: number) =>
        staffTimeGeometry.length > 0
          ? staffFirstNoteX +
            staffPixelXAtTime(staffTimeGeometry, t) -
            staffPixelXAtTime(staffTimeGeometry, scrollAnchorStartTime)
          : anchorStaffPixelX > 0
            ? anchorStaffPixelX + xHighwayRaw(t) - anchorXContent
            : HIGHWAY_MARGIN_LEFT + highwayOffsetX + xHighwayRaw(t);

      const staffXAtTimePlay = (t: number) => {
        let tt = snapLeadingRestStaffTime(t, playbackNotes, scrollAnchorStartTime);
        if (staffTimeGeometry.length > 0) {
          tt = alignPlayheadDivisionToStaffGeometryStart(tt, scrollAnchorStartTime, staffTimeGeometry);
        }
        return staffXAtTimeIntro(tt);
      };

      const highwayXAtTimePlay = (t: number) => {
        let tt = snapLeadingRestStaffTime(t, playbackNotes, scrollAnchorStartTime);
        if (staffTimeGeometry.length > 0) {
          tt = alignPlayheadDivisionToStaffGeometryStart(tt, scrollAnchorStartTime, staffTimeGeometry);
        }
        return highwayXAtTimeIntro(tt);
      };

      const vw = Math.max(
        staffContainer?.clientWidth ?? 0,
        highwayContainer?.clientWidth ?? 0,
        2
      );
      const m0 = preparedSong.measures?.[0];
      const pr = pickupPreRollMs;
      const learnMs = learnPreRollMs;
      const totalPr = pr + learnMs;

      const staffMax = staffContainer
        ? Math.max(0, staffContainer.scrollWidth - staffContainer.clientWidth)
        : Number.POSITIVE_INFINITY;
      const highwayMax = highwayContainer
        ? Math.max(0, highwayContainer.scrollWidth - highwayContainer.clientWidth)
        : Number.POSITIVE_INFINITY;

      /** En reposo al inicio: mostrar la partitura desde la clave (scroll 0), sin calzar el timeline. */
      const atIdleOpening = !isPlayingRef.current && ms <= 1e-3;
      if (atIdleOpening) {
        const contentStartPx =
          staffNotationLeadPx ??
          (staffTimeGeometry.length > 0
            ? staffPixelXAtTime(staffTimeGeometry, scrollAnchorStartTime)
            : staffFirstNoteX || PLAYHEAD_BASE_OFFSET_PX);
        const idle = layoutIdleScoreOpening(contentStartPx);
        setLaneScrollPositions(staffContainer, highwayContainer, idle.staffScrollLeft, idle.highwayScrollLeft);
        setPlayheadLeftPx(idle.playheadLeftPx);
        logPlayhead("Studio.applyScroll", { branch: "idleOpening", contentStartPx, playheadLeftPx: idle.playheadLeftPx });
        return;
      }

      /**
       * Cuenta de entrada (anacrusa): `layoutPlaybackPickupIntro` solo mientras hay sesión activa
       * en el pre-roll (`ms` negativo = antes del 1) o reproduciendo dentro del pre-roll. En reposo
       * con `ms === 0` la aguja debe anclarse a la primera nota (geometría normal), no al pulso 0
       * junto a la clave.
       */
      const inPickupPreRollWindow =
        pickupTotalPulses > 0 && !!m0?.isPickup && pr > 1e-3 && ms < pr - 1e-6;
      const usePickupIntroLayout = inPickupPreRollWindow && (ms < 0 || isPlayingRef.current);

      logPlayhead("Studio.applyScroll", {
        ms,
        pr,
        isPickup: m0?.isPickup,
        pickupTotalPulses,
        playing: isPlayingRef.current,
        inPickupPreRollWindow,
        usePickupIntroLayout,
        staffGeomSamples: staffTimeGeometry.length,
      });

      if (usePickupIntroLayout) {
        const introProgress = ms < 0 ? ms + pr : ms;
        const introLayout = layoutPlaybackPickupIntro(introProgress, vw, {
          introEndMs: pr,
          m0StartDiv: m0.startTime,
          m0LengthDiv: m0.length,
          pickupPulseCount: pickupTotalPulses,
          divisions: preparedSong.divisions,
          beatType: preparedSong.timeSignature.beatType,
          pulseMs: countInPulseMs,
          staffXAtTime: staffXAtTimeIntro,
          highwayXAtTime: highwayXAtTimeIntro,
        });
        if (introLayout) {
          const realStaff = Math.min(introLayout.staffScrollLeft, staffMax);
          const realHighway = Math.min(introLayout.highwayScrollLeft, highwayMax);
          setLaneScrollPositions(staffContainer, highwayContainer, realStaff, realHighway);
          const ph = introLayout.playheadLeftPx + (introLayout.staffScrollLeft - realStaff);
          setPlayheadLeftPx(ph);
          logPlayhead("Studio.applyScroll", { branch: "pickupIntro", introProgress, playheadLeftPx: ph, realStaff });
          return;
        }
      }

      let songMs = Math.max(0, ms - totalPr);
      if (
        learnMs > 1e-3 &&
        ms >= pr - 1e-6 &&
        ms < totalPr - 1e-6
      ) {
        songMs = 0;
      }
      const xStaff = interpolatePlayheadContentX(playbackTimeline, songMs, staffXAtTimePlay);
      const xHighway = interpolatePlayheadContentX(playbackTimeline, songMs, highwayXAtTimePlay);
      const xStaff0 = interpolatePlayheadContentX(playbackTimeline, 0, staffXAtTimePlay);
      const xHighway0 = interpolatePlayheadContentX(playbackTimeline, 0, highwayXAtTimePlay);

      const seg0 = playbackTimeline.segments[0];
      const startMargin = blendedOpeningViewportMarginPx(
        songMs,
        seg0?.endMs,
        PLAYBACK_VIEWPORT_START_MARGIN_PX
      );
      const { staffScrollLeft: wantStaffScroll, highwayScrollLeft: wantHighwayScroll } =
        layoutStaffHighwayScrollForPlayback(xStaff, xStaff0, xHighway, xHighway0, vw, startMargin);

      const realStaff = Math.min(wantStaffScroll, staffMax);
      const realHighway = Math.min(wantHighwayScroll, highwayMax);

      setLaneScrollPositions(staffContainer, highwayContainer, realStaff, realHighway);

      // Aguja única = X visible real de la nota en el pentagrama (referencia de lectura).
      setPlayheadLeftPx(xStaff - realStaff);
      logPlayhead("Studio.applyScroll", {
        branch: "normal",
        songMs,
        xStaff,
        xHighway,
        realStaff,
        playheadLeftPx: xStaff - realStaff,
        startMargin,
      });
    },
    [
      preparedSong,
      playbackTimeline,
      playbackContentLayout,
      pixelsPerBeat,
      highwayOffsetX,
      scrollAnchorStartTime,
      staffFirstNoteX,
      staffNotationLeadPx,
      staffTimeGeometry,
      countInPulseMs,
      pickupTotalPulses,
      pickupPreRollMs,
      learnPreRollMs,
      playbackNotes,
      setLaneScrollPositions,
    ]
  );

  const advance = useCallback(() => {
    setCurrentNoteIndex((prev) => {
      const next = prev + 1;
      if (next >= notesRef.current.length) {
        isPlayingRef.current = false;
        setIsPlaying(false);
        playbackMsRef.current = 0;
        setPlaybackMs(0);
        currentNoteIndexRef.current = 0;
        ytPlayerRef.current?.pause();
        suppressYtPauseEventRef.current = false;
        ytFrozenAtPickupRef.current = false;
        queueMicrotask(() => applyScrollForPlaybackMs(0));
        return 0;
      }
      return next;
    });
  }, [applyScrollForPlaybackMs]);

  const getCurrentLearnExpectedMidis = useCallback(() => {
    const cn = currentNoteRef.current;
    if (!cn || cn.isRest) return [];
    const startTime = cn.startTime;
    return notesRef.current
      .filter((n) => !n.isRest && n.startTime === startTime)
      .map((n) => n.midi);
  }, []);

  const getCurrentLearnEventIndices = useCallback(() => {
    const cn = currentNoteRef.current;
    if (!cn || cn.isRest) return [];
    const startTime = cn.startTime;
    return notesRef.current
      .map((n, idx) => ({ n, idx }))
      .filter(({ n }) => !n.isRest && n.startTime === startTime)
      .map(({ idx }) => idx);
  }, []);

  const nextLearnEventHasSamePitchClass = useCallback(() => {
    const cn = currentNoteRef.current;
    if (!cn || cn.isRest) return false;
    const startTime = cn.startTime;
    const curIdx = currentNoteIndexRef.current;
    const expectedPcs = getCurrentLearnExpectedMidis().map(pitchClass);
    const nextIdx = notesRef.current.findIndex(
      (n, idx) => idx > curIdx && !n.isRest && n.startTime !== startTime
    );
    if (nextIdx < 0 || expectedPcs.length === 0) return false;
    return expectedPcs.includes(pitchClass(notesRef.current[nextIdx].midi));
  }, [getCurrentLearnExpectedMidis]);

  const advanceToNextLearnEvent = useCallback(() => {
    const cn = currentNoteRef.current;
    if (!cn) {
      advance();
      return;
    }
    const startTime = cn.startTime;
    setCurrentNoteIndex((prev) => {
      const next = notesRef.current.findIndex((n, idx) => idx > prev && n.startTime !== startTime);
      if (next < 0) {
        isPlayingRef.current = false;
        setIsPlaying(false);
        if (songId && song) {
          const playable = notesRef.current.filter((n) => !n.isRest);
          const total = Math.max(playable.length, 1);
          appendSongStudioSession({
            songId,
            songTitle: song.title,
            artist: song.artist,
            mode: "learn",
            accuracy: 1,
            hit: total,
            late: 0,
            total,
          });
        }
        setLearnCompleteOpen(true);
        playbackMsRef.current = 0;
        setPlaybackMs(0);
        currentNoteIndexRef.current = 0;
        ytPlayerRef.current?.pause();
        suppressYtPauseEventRef.current = false;
        ytFrozenAtPickupRef.current = false;
        queueMicrotask(() => applyScrollForPlaybackMs(0));
        return 0;
      }
      return next;
    });
  }, [advance, applyScrollForPlaybackMs, songId, song]);

  const syncPlaybackUiToNoteIndex = useCallback(
    (index: number) => {
      const tl = playbackTimelineRef.current;
      if (!tl || notesRef.current.length === 0) return;
      const n = notesRef.current[index];
      if (!n) return;
      const seg = tl.segments.find((s) => s.startTime === n.startTime);
      const songMs = seg?.startMs ?? 0;
      const tpr = totalPreRollMsRef.current;
      const ms = tpr + songMs;
      playbackMsRef.current = ms;
      setPlaybackMs(ms);
      applyScrollForPlaybackMs(ms);

      const yt = ytPlayerRef.current;
      if (youtubeUrlRef.current && yt?.isReady() && modeRef.current === "learn") {
        const ytT = youtubeOffsetRef.current + songMs / 1000;
        suppressYtPauseEventRef.current = true;
        yt.seekTo(ytT, true);
        yt.pause();
      }
    },
    [applyScrollForPlaybackMs],
  );

  useEffect(() => {
    if (!isPlaying || mode !== "learn" || playbackNotes.length === 0) return;
    syncPlaybackUiToNoteIndex(currentNoteIndex);
  }, [currentNoteIndex, isPlaying, mode, playbackNotes.length, syncPlaybackUiToNoteIndex]);

  const {
    state: engineState,
    init,
    prepareForNextNote,
    waitForSilence,
    subscribeToNotes,
    stopRecording,
    detectExpectedPolyphony,
    configureCapture,
  } = useAudioEngine();

  const practiceScoringEnabled = useMemo(
    () => practiceAllowsMicScoring(songDetectionProfile, inputMode),
    [songDetectionProfile, inputMode]
  );

  useEffect(() => {
    configureCapture({ lineIn: inputMode === "line", deviceId });
  }, [inputMode, deviceId, configureCapture]);
  const practiceScoresRef = useRef<PracticeNoteScore[]>([]);
  const practiceEventsRef = useRef<PracticePlaybackEvent[]>([]);
  const [practiceScoring, setPracticeScoring] = useState<PracticeScoringSnapshot>({
    total: 0,
    hit: 0,
    miss: 0,
    late: 0,
    partial: 0,
    pending: 0,
    accuracy: 0,
    currentStatus: "idle",
    eventTotal: 0,
    eventHit: 0,
    eventPartial: 0,
    eventMiss: 0,
  });
  const [learnMicFeedback, setLearnMicFeedback] = useState<{
    status: "idle" | "listening" | "miss" | "correct";
    heard: string | null;
    expected: string | null;
  }>({ status: "idle", heard: null, expected: null });
  const [noteFeedback, setNoteFeedback] = useState<Record<number, "correct" | "wrong">>({});
  const [floatingFeedback, setFloatingFeedback] = useState<{
    id: number;
    tone: "correct" | "wrong" | "streak" | "practice";
    title: string;
    subtitle?: string;
  } | null>(null);
  const [learnCompleteOpen, setLearnCompleteOpen] = useState(false);
  const [practiceCompleteOpen, setPracticeCompleteOpen] = useState(false);
  const learnStreakRef = useRef(0);
  const floatingSeqRef = useRef(0);
  const floatingTimerRef = useRef<number | null>(null);

  const showFloatingFeedback = useCallback(
    (tone: "correct" | "wrong" | "streak" | "practice", title: string, subtitle?: string) => {
      if (floatingTimerRef.current) window.clearTimeout(floatingTimerRef.current);
      setFloatingFeedback({ id: ++floatingSeqRef.current, tone, title, subtitle });
      floatingTimerRef.current = window.setTimeout(() => setFloatingFeedback(null), 1150);
    },
    []
  );

  const markNoteFeedback = useCallback((indices: number[], status: "correct" | "wrong") => {
    if (indices.length === 0) return;
    setNoteFeedback((prev) => {
      const next = { ...prev };
      for (const idx of indices) next[idx] = status;
      return next;
    });
  }, []);

  const resetPracticeScoring = useCallback(() => {
    setPracticeScoring((prev) => {
      if (
        prev.total === 0 &&
        prev.hit === 0 &&
        prev.miss === 0 &&
        prev.pending === 0 &&
        prev.currentStatus === "idle"
      ) {
        return prev;
      }
      return {
        total: 0,
        hit: 0,
        miss: 0,
        late: 0,
        partial: 0,
        pending: 0,
        accuracy: 0,
        currentStatus: "idle",
        eventTotal: 0,
        eventHit: 0,
        eventPartial: 0,
        eventMiss: 0,
      };
    });
  }, []);

  const formatLearnExpectedForEvent = useCallback(() => {
    const midis = getCurrentLearnExpectedMidis();
    if (midis.length <= 1) return songNoteLabel(currentNoteRef.current);
    return formatExpectedEventLabel(midis, (midi) => {
      const sound = midiToNoteName(midi);
      return `${sound.note}${sound.octave}`;
    });
  }, [getCurrentLearnExpectedMidis]);

  useEffect(() => {
    if (mode !== "learn" && mode !== "practice") return;
    void init();
  }, [mode, init]);

  useEffect(() => {
    if (mode !== "learn" && mode !== "practice") return;
    void (async () => {
      try {
        const s = await navigator.mediaDevices.getUserMedia({ audio: true });
        s.getTracks().forEach((t) => t.stop());
      } catch {
        /* sin permiso o sin micrófono */
      }
    })();
  }, [mode]);

  const learnPitchHoldRef = useRef<{ pc: number; since: number } | null>(null);
  const learnEventHitsRef = useRef<Set<number>>(new Set());
  const learnNoteLockRef = useRef<Map<number, number>>(new Map());
  const learnPolyLastAttemptRef = useRef<Map<number, number>>(new Map());
  const learnHeardBufferRef = useRef<HeardPitchSample[]>([]);
  const polyCaptureBusyRef = useRef(false);
  const practiceHeardBufferRef = useRef<HeardPitchSample[]>([]);

  useEffect(() => {
    if (!isPlaying || playbackNotes.length === 0 || mode !== "learn") return;

    let cancelled = false;
    learnPitchHoldRef.current = null;
    learnEventHitsRef.current = new Set();
    learnNoteLockRef.current = new Map();
    learnPolyLastAttemptRef.current = new Map();
    learnHeardBufferRef.current = [];
    learnStreakRef.current = 0;
    setLearnMicFeedback({
      status: "listening",
      heard: null,
      expected: formatLearnExpectedForEvent(),
    });

    const finishLearnPlayback = () => {
      isPlayingRef.current = false;
      setIsPlaying(false);
      if (songId && song) {
        const playable = notesRef.current.filter((n) => !n.isRest);
        const total = Math.max(playable.length, 1);
        appendSongStudioSession({
          songId,
          songTitle: song.title,
          artist: song.artist,
          mode: "learn",
          accuracy: 1,
          hit: total,
          late: 0,
          total,
        });
      }
      setLearnCompleteOpen(true);
      playbackMsRef.current = 0;
      setPlaybackMs(0);
      currentNoteIndexRef.current = 0;
      setCurrentNoteIndex(0);
      ytPlayerRef.current?.pause();
      suppressYtPauseEventRef.current = false;
      ytFrozenAtPickupRef.current = false;
      queueMicrotask(() => applyScrollForPlaybackMs(0));
    };

    const skipRests = () => {
      if (cancelled || !isPlayingRef.current) return;
      const before = currentNoteIndexRef.current;
      const idx = skipLearnRestsSync(notesRef.current, before);
      if (idx < 0) {
        finishLearnPlayback();
        return;
      }
      if (idx === before) return;
      currentNoteIndexRef.current = idx;
      setCurrentNoteIndex(idx);
      syncPlaybackUiToNoteIndex(idx);
      prepareForNextNote();
      learnPitchHoldRef.current = null;
      learnEventHitsRef.current = new Set();
      learnNoteLockRef.current = new Map();
      learnPolyLastAttemptRef.current = new Map();
      learnHeardBufferRef.current = [];
      setLearnMicFeedback({
        status: "listening",
        heard: null,
        expected: formatLearnExpectedForEvent(),
      });
    };
    skipRests();

    const completeLearnEvent = () => {
      const cn = currentNoteRef.current;
      if (!cn || cn.isRest) return;
      const tl = playbackTimelineRef.current;
      const durationMs = tl ? getEventDurationMs(tl, cn) : 400;
      const voices = getCurrentLearnEventIndices().length;
      const profile = getLearnDetectionProfile(
        durationMs,
        nextLearnEventHasSamePitchClass(),
        voices
      );

      learnEventHitsRef.current = new Set();
      learnNoteLockRef.current = new Map();
      learnPolyLastAttemptRef.current = new Map();
      learnHeardBufferRef.current = [];
      learnPitchHoldRef.current = null;
      learnStreakRef.current += 1;
      const streak = learnStreakRef.current;
      if ([3, 5, 10, 20].includes(streak)) {
        showFloatingFeedback("streak", `Racha x${streak}`, "¡Sigue así!");
      } else {
        showFloatingFeedback("correct", "Correcto", "Muy bien");
      }
      setLearnMicFeedback({
        status: "correct",
        heard: null,
        expected: formatLearnExpectedForEvent(),
      });
      advanceToNextLearnEvent();
      prepareForNextNote();
      if (profile.silenceMinMs > 0) {
        void waitForSilence({
          minMs: profile.silenceMinMs,
          timeoutMs: profile.silenceTimeoutMs,
        });
      }
    };

    const registerLearnHit = (note: MonoDetectedNote) => {
      const cn = currentNoteRef.current;
      if (!cn || cn.isRest) return;
      const eventIndices = getCurrentLearnEventIndices();
      const now = performance.now();

      const hitIdx = findLearnNoteIndexForHeardPitch(
        eventIndices,
        notesRef.current,
        note.midi,
        note.cents,
        learnEventHitsRef.current,
        learnPitchMatches
      );
      if (hitIdx === null) return;

      const noteLockUntil = learnNoteLockRef.current.get(hitIdx);
      if (noteLockUntil !== undefined && now < noteLockUntil) return;

      const tl = playbackTimelineRef.current;
      const durationMs = tl ? getEventDurationMs(tl, cn) : 400;
      const profile = getLearnDetectionProfile(
        durationMs,
        nextLearnEventHasSamePitchClass(),
        eventIndices.length
      );
      learnNoteLockRef.current.set(hitIdx, now + profile.lockMs);
      learnPitchHoldRef.current = null;

      learnEventHitsRef.current.add(hitIdx);
      markNoteFeedback([hitIdx], "correct");

      const poly = eventIndices.length >= 2;
      if (poly && !isLearnEventComplete(eventIndices, learnEventHitsRef.current)) {
        const progress = `${learnEventHitsRef.current.size}/${eventIndices.length}`;
        showFloatingFeedback("correct", "Voz correcta", `Acorde ${progress}`);
        setLearnMicFeedback({
          status: "listening",
          heard: `${note.note}${note.octave}`,
          expected: formatLearnExpectedForEvent(),
        });
        return;
      }

      completeLearnEvent();
    };

    const tryFillLearnChordFromBuffer = (): boolean => {
      const eventIndices = getCurrentLearnEventIndices();
      if (eventIndices.length < 2) return false;
      let any = false;
      for (const idx of eventIndices) {
        if (learnEventHitsRef.current.has(idx)) continue;
        const n = notesRef.current[idx];
        if (!n || n.isRest) continue;
        const hit = learnHeardBufferRef.current.some((s) =>
          learnPitchMatches(s.midi, n.midi, s.cents)
        );
        if (!hit) continue;
        learnEventHitsRef.current.add(idx);
        markNoteFeedback([idx], "correct");
        any = true;
      }
      if (any && isLearnEventComplete(eventIndices, learnEventHitsRef.current)) {
        completeLearnEvent();
        return true;
      }
      if (any) {
        showFloatingFeedback(
          "correct",
          "Acorde",
          `${learnEventHitsRef.current.size}/${eventIndices.length} voces`
        );
      }
      return any;
    };

    const unsubscribe = subscribeToNotes({
      threshold: 0.0015,
      holdTimeMs: 0,
      fastOnset: true,
      onNote: (note) => {
        if (cancelled || !isPlayingRef.current || modeRef.current !== "learn") return;
        skipRests();

        const cn = currentNoteRef.current;
        if (!cn || cn.isRest) return;
        const expectedMidis = getCurrentLearnExpectedMidis();
        const expected = formatLearnExpectedForEvent();
        const heard = `${note.note}${note.octave}`;
        if (expectedMidis.length === 0) {
          setLearnMicFeedback({ status: "listening", heard, expected });
          return;
        }

        const eventIndices = getCurrentLearnEventIndices();
        const pendingIdx = findLearnNoteIndexForHeardPitch(
          eventIndices,
          notesRef.current,
          note.midi,
          note.cents,
          learnEventHitsRef.current,
          learnPitchMatches
        );
        const matches = pendingIdx !== null;
        setLearnMicFeedback({
          status: matches ? "listening" : "miss",
          heard,
          expected,
        });

        if (!matches) {
          learnPitchHoldRef.current = null;
          return;
        }

        recordHeardPitch(learnHeardBufferRef.current, {
          tMs: performance.now(),
          midi: note.midi,
          cents: note.cents,
        });
        if (eventIndices.length >= 2 && tryFillLearnChordFromBuffer()) {
          return;
        }

        const now = performance.now();
        const noteLockUntil = learnNoteLockRef.current.get(pendingIdx);
        if (noteLockUntil !== undefined && now < noteLockUntil) return;

        const tl = playbackTimelineRef.current;
        const durationMs = tl ? getEventDurationMs(tl, cn) : 400;
        const profile = getLearnDetectionProfile(
          durationMs,
          nextLearnEventHasSamePitchClass(),
          eventIndices.length
        );
        const pc = pitchClass(note.midi);
        if (!learnPitchHoldRef.current || learnPitchHoldRef.current.pc !== pc) {
          learnPitchHoldRef.current = { pc, since: now };
        }
        const heldMs = now - learnPitchHoldRef.current.since;
        if (heldMs >= profile.holdTimeMs) {
          const poly = eventIndices.length >= 2;
          const attackTime = cn.startTime;
          if (
            poly &&
            shouldTriggerLearnPolyCapture(
              eventIndices,
              learnEventHitsRef.current,
              heldMs,
              learnPolyLastAttemptRef.current.get(attackTime) ?? 0,
              polyCaptureBusyRef.current || engineState.isAnalyzing
            )
          ) {
            learnPolyLastAttemptRef.current.set(attackTime, performance.now());
            polyCaptureBusyRef.current = true;
            void detectExpectedPolyphony({
              expectedMidis,
              noteIndices: eventIndices,
              captureMs: learnPolyCaptureDurationMs(durationMs),
            })
              .then((result) => {
                if (cancelled) return;
                for (const idx of result.matchedNoteIndices) {
                  learnEventHitsRef.current.add(idx);
                  markNoteFeedback([idx], "correct");
                }
                if (isLearnEventComplete(eventIndices, learnEventHitsRef.current)) {
                  completeLearnEvent();
                } else if (result.matchedNoteIndices.length > 0) {
                  tryFillLearnChordFromBuffer();
                  setLearnMicFeedback({
                    status: "listening",
                    heard: `${note.note}${note.octave}`,
                    expected: formatLearnExpectedForEvent(),
                  });
                }
              })
              .finally(() => {
                polyCaptureBusyRef.current = false;
              });
          }
          registerLearnHit(note);
        }
      },
    });

    return () => {
      cancelled = true;
      unsubscribe();
      stopRecording();
      learnPitchHoldRef.current = null;
      setLearnMicFeedback({ status: "idle", heard: null, expected: null });
    };
  }, [
    isPlaying,
    playbackNotes.length,
    mode,
    advanceToNextLearnEvent,
    getCurrentLearnExpectedMidis,
    getCurrentLearnEventIndices,
    nextLearnEventHasSamePitchClass,
    markNoteFeedback,
    showFloatingFeedback,
    detectExpectedPolyphony,
    engineState.isAnalyzing,
    engineState.isReady,
    formatLearnExpectedForEvent,
    prepareForNextNote,
    waitForSilence,
    subscribeToNotes,
    stopRecording,
  ]);

  useEffect(() => {
    const tl = playbackTimelineRef.current;
    if (
      !isPlaying ||
      playbackNotes.length === 0 ||
      mode !== "practice" ||
      !tl
    ) {
      practiceScoresRef.current = [];
      practiceEventsRef.current = [];
      resetPracticeScoring();
      return;
    }

    let cancelled = false;
    let unsubscribe: (() => void) | null = null;
    let intervalId = 0;
    practiceHeardBufferRef.current = [];

    const syncPracticeFeedback = () => {
      const scores = practiceScoresRef.current;
      const events = practiceEventsRef.current;
      refreshEventStatuses(events, scores);
      const hit = scores
        .filter((score) => score.status === "hit" || score.status === "late")
        .map((score) => score.noteIndex);
      const missed = scores.filter((score) => score.status === "miss").map((score) => score.noteIndex);
      markNoteFeedback(hit, "correct");
      markNoteFeedback(missed, "wrong");
      return summarizePracticeScores(scores, currentNoteIndexRef.current, events);
    };

    const applyPolyCaptureResult = (songMs: number, ev: PracticePlaybackEvent, matched: number[]) => {
      if (matched.length === 0) return false;
      const end = eventWindowEndMs(ev);
      const isLate = songMs > end;
      let any = false;
      for (const idx of matched) {
        const s = practiceScoresRef.current.find((n) => n.noteIndex === idx);
        if (s && s.status === "pending") {
          s.status = isLate ? "late" : "hit";
          any = true;
        }
      }
      return any;
    };

    const attachPracticeListeners = () => {
      if (cancelled) return;
      practiceScoresRef.current = buildPracticeNoteScores(notesRef.current, tl);
      practiceEventsRef.current = buildPracticePlaybackEvents(notesRef.current, tl);
      setPracticeScoring(
        summarizePracticeScores(
          practiceScoresRef.current,
          currentNoteIndexRef.current,
          practiceEventsRef.current
        )
      );

      unsubscribe = subscribeToNotes({
      threshold: 0.0015,
      holdTimeMs: 0,
      fastOnset: true,
      onNote: (note) => {
        if (cancelled || !isPlayingRef.current || modeRef.current !== "practice") return;
        const songMs = Math.max(0, playbackMsRef.current - totalPreRollMsRef.current);
        const scoredIndex = getPlaybackNoteState(tl, songMs).noteIndex;
        if (note.volume > 0.004 && note.confidence > 0.2) {
          recordHeardPitch(practiceHeardBufferRef.current, {
            tMs: songMs,
            midi: note.midi,
            cents: note.cents,
          });
        }

        let polyChanged = false;
        const ev = getEventForNoteIndex(
          practiceEventsRef.current,
          scoredIndex
        );
        if (ev && isPolyphonicEvent(ev)) {
          const matched = matchPendingNoteIndicesFromHeard(
            ev,
            practiceScoresRef.current,
            practiceHeardBufferRef.current,
            songMs
          );
          polyChanged = applyPolyCaptureResult(songMs, ev, matched);
        }

        const changed =
          scorePracticeFrame(
            practiceScoresRef.current,
            {
              tMs: songMs,
              midi: note.midi,
              confidence: note.confidence,
              volume: note.volume,
              cents: note.cents,
            },
            {
              currentNoteIndex: scoredIndex,
              latencyOffsetMs: DEFAULT_PITCH_LATENCY_MS,
            }
          ) || polyChanged;

        if (changed) {
          setPracticeScoring(syncPracticeFeedback());
        }
      },
    });

      intervalId = window.setInterval(() => {
      if (cancelled || !isPlayingRef.current || modeRef.current !== "practice") return;
      const songMs = Math.max(0, playbackMsRef.current - totalPreRollMsRef.current);
      const changed = finalizeExpiredPracticeEvents(
        practiceEventsRef.current,
        practiceScoresRef.current,
        songMs
      );

      if (changed) {
        const snapshot = syncPracticeFeedback();
        setPracticeScoring(snapshot);
        if (snapshot.eventPartial > 0) {
          showFloatingFeedback("wrong", "Casi", `${snapshot.eventPartial} acorde(s) incompleto(s)`);
        } else {
          showFloatingFeedback("wrong", "Atento", "Hay notas por corregir");
        }
      }
    }, 90);
    };

    const bootstrapRaf = requestAnimationFrame(attachPracticeListeners);

    return () => {
      cancelled = true;
      cancelAnimationFrame(bootstrapRaf);
      if (intervalId) window.clearInterval(intervalId);
      unsubscribe?.();
    };
  }, [
    isPlaying,
    playbackNotes.length,
    mode,
    subscribeToNotes,
    resetPracticeScoring,
    markNoteFeedback,
    showFloatingFeedback,
    practiceScoringEnabled,
  ]);

  useEffect(() => {
    const onResize = () => {
      applyScrollForPlaybackMs(playbackMsRef.current);
    };
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [applyScrollForPlaybackMs]);

  useEffect(() => {
    let rafId = 0;
    let cancelled = false;

    const tick = () => {
      if (cancelled) return;

      const tl = playbackTimelineRef.current;
      if (
        isPlayingRef.current &&
        tl &&
        playbackNotes.length > 0 &&
        modeRef.current === "practice"
      ) {
        const elapsed = performance.now() - playStartWallRef.current;
        const tpr = totalPreRollMsRef.current;
        const totalSession = tpr + tl.totalMs;
        const raw = playStartMsRef.current + elapsed;
        let capped = Math.min(totalSession, raw);

        const yt = ytPlayerRef.current;
        if (youtubeUrlRef.current && yt?.isReady() && modeRef.current === "practice") {
          const off = youtubeOffsetRef.current;
          if (tpr > 1e-3 && capped < tpr - 1e-4) {
            if (!ytFrozenAtPickupRef.current) {
              suppressYtPauseEventRef.current = true;
              yt.seekTo(off, true);
              yt.pause();
              ytFrozenAtPickupRef.current = true;
            }
          } else {
            const ytT = yt.getCurrentTime();
            const songMs = Math.max(0, (ytT - off) * 1000);
            capped = Math.min(totalSession, tpr + songMs);
            if (ytFrozenAtPickupRef.current) {
              yt.resumeVideo();
              ytFrozenAtPickupRef.current = false;
            }
          }
        }

        playbackMsRef.current = capped;

        const songMs = Math.max(0, capped - tpr);
        const visualSongMs = Math.min(tl.totalMs, songMs + PRACTICE_PLAYHEAD_LOOKAHEAD_MS);
        const { noteIndex } = getPlaybackNoteState(tl, visualSongMs);
        const indexChanged = noteIndex !== currentNoteIndexRef.current;
        if (indexChanged) {
          currentNoteIndexRef.current = noteIndex;
          setCurrentNoteIndex(noteIndex);
        }

        const now = performance.now();
        if (indexChanged || now - playbackUiFlushRef.current >= 33) {
          playbackUiFlushRef.current = now;
          setPlaybackMs(capped);
        }

        const visualPlaybackMs = Math.min(totalSession, tpr + visualSongMs);
        if (
          indexChanged ||
          now - playbackLaneScrollFlushRef.current >= 32
        ) {
          playbackLaneScrollFlushRef.current = now;
          applyScrollForPlaybackMs(visualPlaybackMs);
        }

        if (capped >= totalSession - 1e-4) {
          finalizeExpiredPracticeEvents(
            practiceEventsRef.current,
            practiceScoresRef.current,
            tl.totalMs + 10000
          );
          refreshEventStatuses(practiceEventsRef.current, practiceScoresRef.current);
          const credited = practiceScoresRef.current
            .filter((score) => score.status === "hit" || score.status === "late")
            .map((score) => score.noteIndex);
          markNoteFeedback(credited, "correct");
          const missed = practiceScoresRef.current
            .filter((score) => score.status === "miss")
            .map((score) => score.noteIndex);
          markNoteFeedback(missed, "wrong");
          const practiceSummary = summarizePracticeScores(
            practiceScoresRef.current,
            currentNoteIndexRef.current,
            practiceEventsRef.current
          );
          setPracticeScoring(practiceSummary);
          setPracticeCompleteSummary(practiceSummary);
          setTaskSubmitMessage(null);
          if (
            songId === TASK_SONG_ROUTE_ID &&
            taskSongRef.current?.taskId &&
            taskSongRef.current.teacherId &&
            profileRef.current
          ) {
            const pct = practiceSummary.total > 0
              ? practiceSummary.accuracy
              : practiceSummary.eventTotal > 0
                ? (practiceSummary.eventHit + practiceSummary.eventPartial * 0.5) /
                  practiceSummary.eventTotal
                : 0;
            void submitResult(
              taskSongRef.current.taskId,
              taskSongRef.current.teacherId,
              {
                timestamp: Date.now(),
                score: pct,
                detectedNotes: [],
                expectedNotes: [],
                feedback: `Práctica: ${Math.round(pct * 100)}%`,
              },
              profileRef.current
            ).then((ok) => {
              setTaskSubmitMessage(
                ok ? "Resultado enviado al profesor" : "No se pudo guardar el resultado"
              );
            });
          }
          if (songId && song) {
            appendSongStudioSession({
              songId,
              songTitle: song.title,
              artist: song.artist,
              mode: "practice",
              accuracy: practiceSummary.accuracy,
              hit: practiceSummary.hit,
              late: practiceSummary.late,
              total: Math.max(practiceSummary.total, 1),
            });
          }
          isPlayingRef.current = false;
          setIsPlaying(false);
          setPracticeCompleteOpen(true);
          setPlaybackMs(0);
          playbackMsRef.current = 0;
          const resetIdx = tl.segments[0]?.noteIndex ?? 0;
          currentNoteIndexRef.current = resetIdx;
          setCurrentNoteIndex(resetIdx);
          applyScrollForPlaybackMs(0);
          ytFrozenAtPickupRef.current = false;
          suppressYtPauseEventRef.current = false;
          ytPlayerRef.current?.pause();
        }
        rafId = requestAnimationFrame(tick);
      }
    };

    if (isPlaying && playbackNotes.length > 0 && mode === "practice") {
      playStartWallRef.current = performance.now();
      let start = playbackMsRef.current;
      const pr = pickupPreRollMsRef.current;
      const m0 = preparedSongRef.current?.measures?.[0];
      if (start <= 1e-3 && pr > 1e-3 && m0?.isPickup) {
        start = -pr;
      }
      playStartMsRef.current = start;
      rafId = requestAnimationFrame(tick);
    }

    return () => {
      cancelled = true;
      cancelAnimationFrame(rafId);
    };
  }, [isPlaying, playbackNotes.length, mode, applyScrollForPlaybackMs, markNoteFeedback]);

  const idleScrollRafRef = useRef<number | null>(null);
  useLayoutEffect(() => {
    if (isPlaying) return;
    if (idleScrollRafRef.current !== null) cancelAnimationFrame(idleScrollRafRef.current);
    idleScrollRafRef.current = requestAnimationFrame(() => {
      idleScrollRafRef.current = null;
      applyScrollForPlaybackMs(playbackMs);
    });
    return () => {
      if (idleScrollRafRef.current !== null) cancelAnimationFrame(idleScrollRafRef.current);
    };
  }, [playbackMs, isPlaying, applyScrollForPlaybackMs, staffTimeGeometry]);

  /** Re-alinea scroll cuando el mástil termina de montar o el ancho del pentagrama está listo. */
  useEffect(() => {
    if (!heavySurfaceReady || !preparedSong) return;
    queueMicrotask(() => applyScrollForPlaybackMs(playbackMsRef.current));
  }, [heavySurfaceReady, staffScrollContentWidth, staffTimeGeometry.length, preparedSong, applyScrollForPlaybackMs]);

  const handlePlayPause = () => {
    if (isPlaying) {
      isPlayingRef.current = false;
      setIsPlaying(false);
      playStartMsRef.current = playbackMsRef.current;
      suppressYtPauseEventRef.current = false;
      ytFrozenAtPickupRef.current = false;
      ytPlayerRef.current?.pause();
    } else {
      setLearnCompleteOpen(false);
      setPracticeCompleteOpen(false);
      setNoteFeedback({});
      learnStreakRef.current = 0;
      playStartWallRef.current = performance.now();
      let start = playbackMsRef.current;
      const pr = pickupPreRollMsRef.current;
      const m0 = preparedSongRef.current?.measures?.[0];
      const tpr = totalPreRollMsRef.current;
      const yt = ytPlayerRef.current;
      const hasYt = Boolean(song?.youtubeUrl && yt?.isReady());

      if (mode === "learn") {
        const atFullStart = playbackMsRef.current <= 1e-3;
        if (atFullStart) {
          setCurrentNoteIndex(0);
          currentNoteIndexRef.current = 0;
          setPlaybackMs(0);
          playbackMsRef.current = 0;
          start = pr > 1e-3 && m0?.isPickup ? -pr : 0;
        } else if (start <= 1e-3 && pr > 1e-3 && m0?.isPickup) {
          start = -pr;
        }
        if (hasYt && yt) {
          const songMs = Math.max(0, playbackMsRef.current - tpr);
          suppressYtPauseEventRef.current = true;
          yt.seekTo(youtubeOffsetRef.current + songMs / 1000, true);
          yt.pause();
        }
        ytFrozenAtPickupRef.current = false;
      } else {
        if (start <= 1e-3 && pr > 1e-3 && m0?.isPickup) {
          start = -pr;
        }
        if (hasYt && yt) {
          const sessionMs = playbackMsRef.current;
          const songMs = Math.max(0, sessionMs - tpr);
          suppressYtPauseEventRef.current = true;
          yt.seekTo(youtubeOffsetRef.current + songMs / 1000, true);
          const willFreezeForPickup = tpr > 1e-3 && sessionMs < tpr - 1e-4;
          if (willFreezeForPickup) {
            ytFrozenAtPickupRef.current = true;
            yt.pause();
          } else {
            ytFrozenAtPickupRef.current = false;
            Promise.resolve().then(() => {
              try {
                yt.resumeVideo();
              } catch (e) {
                console.warn("[SongStudio] YT resume failed:", e);
              }
            });
          }
        } else {
          ytFrozenAtPickupRef.current = false;
        }
      }

      playStartMsRef.current = start;
      isPlayingRef.current = true;
      playbackLaneScrollFlushRef.current = 0;
      setIsPlaying(true);
    }
  };

  const handleStop = () => {
    suppressYtPauseEventRef.current = false;
    ytFrozenAtPickupRef.current = false;
    isPlayingRef.current = false;
    setIsPlaying(false);
    setPlaybackMs(0);
    playbackMsRef.current = 0;
    setCurrentNoteIndex(0);
    currentNoteIndexRef.current = 0;
    setNoteFeedback({});
    setLearnCompleteOpen(false);
    setPracticeCompleteOpen(false);
    learnStreakRef.current = 0;
    applyScrollForPlaybackMs(0);
    ytPlayerRef.current?.pause();
  };

  const restartLearn = () => {
    setMode("learn");
    setLearnCompleteOpen(false);
    setNoteFeedback({});
    learnStreakRef.current = 0;
    currentNoteIndexRef.current = 0;
    setCurrentNoteIndex(0);
    playbackMsRef.current = 0;
    setPlaybackMs(0);
    applyScrollForPlaybackMs(0);
    playStartMsRef.current = 0;
    playStartWallRef.current = performance.now();
    isPlayingRef.current = true;
    setIsPlaying(true);
  };

  const startPracticeFromComplete = () => {
    setMode("practice");
    setLearnCompleteOpen(false);
    setNoteFeedback({});
    currentNoteIndexRef.current = 0;
    setCurrentNoteIndex(0);
    playbackMsRef.current = 0;
    setPlaybackMs(0);
    applyScrollForPlaybackMs(0);
    playStartMsRef.current = 0;
    playStartWallRef.current = performance.now();
    isPlayingRef.current = true;
    setIsPlaying(true);
  };

  const restartPractice = () => {
    setMode("practice");
    setPracticeCompleteOpen(false);
    setPracticeCompleteSummary(null);
    setTaskSubmitMessage(null);
    setNoteFeedback({});
    resetPracticeScoring();
    currentNoteIndexRef.current = 0;
    setCurrentNoteIndex(0);
    playbackMsRef.current = 0;
    setPlaybackMs(0);
    applyScrollForPlaybackMs(0);
    playStartMsRef.current = 0;
    playStartWallRef.current = performance.now();
    isPlayingRef.current = true;
    setIsPlaying(true);
  };

  const reviewInLearnMode = () => {
    setMode("learn");
    setPracticeCompleteOpen(false);
    setLearnCompleteOpen(false);
    setNoteFeedback({});
    currentNoteIndexRef.current = 0;
    setCurrentNoteIndex(0);
    playbackMsRef.current = 0;
    setPlaybackMs(0);
    applyScrollForPlaybackMs(0);
    playStartMsRef.current = 0;
    playStartWallRef.current = performance.now();
    isPlayingRef.current = true;
    setIsPlaying(true);
  };

  const starterLoading = Boolean(starterMeta && !starterXml && !starterLoadError);

  if (starterLoading || (songId !== TASK_SONG_ROUTE_ID && !starterMeta && loading)) {
    return wrapStudio(
      <div className="h-full w-full min-h-[40vh] flex items-center justify-center">
        <div className="w-8 h-8 rounded-full border-2 border-t-transparent animate-spin" style={{ borderColor: C.accent, borderTopColor: "transparent" }} />
      </div>
    );
  }

  const isTaskSong = songId === TASK_SONG_ROUTE_ID;

  if (!song) {
    return wrapStudio(
      <div className="h-full w-full min-h-[40vh] flex flex-col items-center justify-center gap-4">
        <Music className="w-12 h-12" style={{ color: C.textDim }} />
        <p style={{ color: C.textMuted }}>
          {starterLoadError
            ? starterLoadError
            : isTaskSong
            ? "No se pudo cargar la tarea. Vuelve al panel e ingresa el código de nuevo."
            : "Canción no encontrada"}
        </p>
        <button
          onClick={() =>
            navigate(
              pathMode
                ? pathExit
                : isTaskSong
                  ? "/home"
                  : starterMeta
                    ? "/play"
                    : "/songs"
            )
          }
          className="text-sm font-medium transition-colors"
          style={{ color: C.accent }}
        >
          {pathMode
            ? "Volver al camino"
            : isTaskSong
              ? "Volver al panel"
              : starterMeta
                ? "Volver a Tocar"
                : "Volver a la biblioteca"}
        </button>
      </div>
    );
  }

  return wrapStudio(
    <div className="h-full flex flex-col min-h-[min(72vh,720px)]">
      {/* Zen App Bar */}
      <div className="shrink-0 flex items-center justify-between px-5 py-3 border-b" style={{ borderColor: C.border, backgroundColor: C.bgElevated }}>
        <div className="flex items-center gap-3 min-w-0">
          <button
            onClick={() => navigate(pathMode ? pathExit : "/songs")}
            className="w-8 h-8 rounded-lg flex items-center justify-center transition-colors hover:bg-black/5 shrink-0"
            aria-label={pathMode ? "Volver al camino" : "Volver a la biblioteca"}
          >
            <ArrowLeft className="w-4 h-4" style={{ color: C.textMuted }} />
          </button>
          <div className="min-w-0">
            {pathMode && (
              <p className="text-[10px] uppercase tracking-[0.14em] mb-0.5" style={{ color: "#99f6e4" }}>
                Do mayor · Estrellita
              </p>
            )}
            <h1 className="text-sm font-medium truncate" style={{ color: C.textMain }}>{song.title}</h1>
            <p className="text-[11px] truncate" style={{ color: C.textDim }}>
              {song.artist || "Artista desconocido"} · {song.tempo || preparedSong?.tempo || 120} BPM
              {songDetectionProfile.classification === "dense-poly"
                ? " · Acordes densos"
                : songDetectionProfile.polyEventCount > 0
                  ? " · Melodía y acordes"
                  : ""}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2 shrink-0">
          {songId === TASK_SONG_ROUTE_ID && song.musicXml && !pathMode && (
            <button
              type="button"
              disabled={savedTaskToLibrary}
              onClick={async () => {
                if (!song.musicXml) return;
                await saveSong({
                  title: song.title,
                  artist: "Profesor",
                  musicXml: song.musicXml,
                  youtubeUrl: song.youtubeUrl,
                  tempo: song.tempo,
                });
                setSavedTaskToLibrary(true);
              }}
              className="rounded-lg px-3 py-1.5 text-xs font-semibold text-white disabled:opacity-50"
              style={{ backgroundColor: "#14B8A6" }}
            >
              {savedTaskToLibrary ? "Guardada" : "Guardar en biblioteca"}
            </button>
          )}
          {isTeacher && !isTaskSong && song.musicXml && !pathMode && (
            <TaskCreatorButton
              type="song"
              title={song.title}
              description={
                song.artist
                  ? `${song.title} — ${song.artist}`
                  : `Pieza MusicXML: ${song.title}`
              }
              config={serializeSongState(song.title, song.musicXml, {
                youtubeUrl: song.youtubeUrl,
                tempo: song.tempo ?? preparedSong?.tempo,
              })}
              variant="studio"
            />
          )}
          <button onClick={() => setShowVideo((v) => !v)} className="w-8 h-8 rounded-lg flex items-center justify-center transition-colors hover:bg-black/5" title="Toggle video">
            {showVideo ? <Eye className="w-4 h-4" style={{ color: C.textMuted }} /> : <EyeOff className="w-4 h-4" style={{ color: C.textDim }} />}
          </button>
          <button onClick={() => setShowSettings(true)} className="w-8 h-8 rounded-lg flex items-center justify-center transition-colors hover:bg-black/5">
            <Settings className="w-4 h-4" style={{ color: C.textMuted }} />
          </button>
        </div>
      </div>

        {/* Main Canvas — degradado + ondas + partículas (estilo aprendizaje) */}
        <SongLearnCanvasBackdrop ambient="lite" className="flex-1 min-h-0">
          <div className="relative flex min-h-0 flex-1 flex-col">
            {playbackNotes.length > 0 && (
              <SongInputGuidanceBanner
                profile={songDetectionProfile}
                mode={mode}
                inputMode={inputMode}
                practiceScoringEnabled={practiceScoringEnabled}
                onOpenSettings={() => setShowSettings(true)}
              />
            )}
            <AnimatePresence>
              {floatingFeedback && (
                <motion.div
                  key={floatingFeedback.id}
                  initial={{ opacity: 0, y: 18, scale: 0.86 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  exit={{ opacity: 0, y: -18, scale: 0.92 }}
                  transition={{ type: "spring", stiffness: 420, damping: 26 }}
                  className="pointer-events-none absolute left-1/2 top-14 z-50 -translate-x-1/2 text-center"
                >
                  <div
                    className="rounded-3xl px-8 py-4 shadow-2xl"
                    style={{
                      background:
                        floatingFeedback.tone === "wrong"
                          ? "linear-gradient(135deg, rgba(251,113,133,0.94), rgba(190,18,60,0.9))"
                          : floatingFeedback.tone === "streak"
                            ? "linear-gradient(135deg, rgba(251,191,36,0.96), rgba(217,119,6,0.92))"
                            : "linear-gradient(135deg, rgba(52,211,153,0.95), rgba(20,184,166,0.9))",
                      color: "#fff",
                      textShadow: "0 2px 14px rgba(0,0,0,0.35)",
                      border: "1px solid rgba(255,255,255,0.28)",
                    }}
                  >
                    <div className="font-serif text-3xl font-black tracking-wide">
                      {floatingFeedback.title}
                    </div>
                    {floatingFeedback.subtitle && (
                      <div className="mt-1 text-sm font-semibold opacity-90">{floatingFeedback.subtitle}</div>
                    )}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
            <div className="flex min-h-0 flex-1 flex-col">
              <AnimatePresence>
                {showStaff && preparedSong && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: staffViewportHeightPx, opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    className="relative shrink-0 w-full overflow-hidden"
                    style={{
                      height: staffViewportHeightPx,
                      paddingTop: DEFAULT_SONG_VIEW_STAFF_TOP_PADDING_PX,
                    }}
                  >
                    <PlaybackPlayheadOverlay
                      accent={C.accent}
                      accentGlow={C.accentGlow}
                      leftOffsetPx={playheadLeftPx}
                      topInsetPx={SONG_PLAYHEAD_BLEED_PX}
                      bottomInsetPx={SONG_PLAYHEAD_BLEED_PX}
                      needleWidthPx={SONG_PLAYHEAD_WIDTH_PX}
                      trailMaxPx={140}
                      className="z-30"
                    />
                  {isPlaying &&
                    pickupTotalPulses > 0 &&
                    pickupPreRollMs > 1e-3 &&
                    playbackMs < pickupPreRollMs - 0.5 && (
                    <BeatCountInOverlay
                      introEndMs={pickupPreRollMs}
                      playbackMs={playbackMs < 0 ? playbackMs + pickupPreRollMs : playbackMs}
                      pulseMs={countInPulseMs}
                      beatType={preparedSong.timeSignature.beatType}
                      darkMode
                      accent={C.accent}
                      anchorLeftPx={playheadLeftPx}
                      pickupLeadIn={Boolean(preparedSong.measures[0]?.isPickup)}
                      pickupTotalPulses={pickupTotalPulses}
                    />
                  )}
                  {isPlaying &&
                    learnPreRollMs > 1e-3 &&
                    !preparedSong.measures[0]?.isPickup &&
                    playbackMs < learnPreRollMs - 0.5 && (
                      <BeatCountInOverlay
                        introEndMs={learnPreRollMs}
                        playbackMs={playbackMs}
                        pulseMs={countInPulseMs}
                        beatType={preparedSong.timeSignature.beatType}
                        darkMode
                        accent={C.accent}
                        anchorLeftPx={playheadLeftPx}
                        pickupLeadIn={false}
                        pickupTotalPulses={0}
                        standardMeasureBeatCount={preparedSong.timeSignature.beats}
                      />
                    )}
                    <SongStaffRenderer
                      _ref={staffScrollRef}
                      notes={preparedSong.notes}
                      divisions={preparedSong.divisions}
                      timeSignature={preparedSong.timeSignature}
                      activeStartTime={activeStartTime}
                      currentNoteIndex={currentNoteIndex}
                      darkMode
                      clefOctaveChange={preparedSong.clefOctaveChange}
                      xmlClefOctaveChange={preparedSong.xmlClefOctaveChange}
                      measureInfos={preparedSong.measures}
                      pixelsPerBeat={pixelsPerBeat}
                      onStaffTimeGeometry={handleStaffTimeGeometry}
                      onStaffLayoutMeta={handleStaffLayoutMeta}
                      hidePlayhead
                      showBassStaff={needsBassStaff}
                      activeGlowAccent={C.accent}
                      noteFeedback={noteFeedback}
                    />
                  </motion.div>
                )}
              </AnimatePresence>

              <div className="min-h-0 flex-1" aria-hidden />

              <div
                className="mt-auto shrink-0 flex flex-col overflow-hidden"
                style={{
                  flex: surface === "keyboard" ? "0 0 auto" : "1 1 auto",
                  minHeight: surface === "keyboard" ? undefined : 140,
                  maxHeight: surface === "keyboard" ? "42vh" : undefined,
                }}
              >
            {preparedSong && surface === "keyboard" && (
              <div className="relative z-40 flex justify-center px-2 pb-2 pointer-events-none">
                <div className="pointer-events-auto w-full max-w-[640px]">
                  <TransportBar
                    isPlaying={isPlaying}
                    progressPercent={playbackProgressPercent}
                    eventIndex={playbackEventIndex}
                    totalEvents={playbackTimeline?.segments.length ?? 0}
                    bpm={bpm}
                    onPlayPause={handlePlayPause}
                    onStop={handleStop}
                    onBpmChange={setBpm}
                    currentNote={currentNote}
                  />
                </div>
              </div>
            )}
            <div className="overflow-hidden flex flex-col min-h-0">
            {preparedSong ? (
              surface === "keyboard" ? (
                heavySurfaceReady ? (
                <SongPianoHighway
                  notes={playbackNotes}
                  currentNoteIndex={currentNoteIndex}
                  activeStartTime={activeStartTime}
                  isPlaying={isPlaying}
                  noteFeedback={noteFeedback}
                  lookaheadGroups={1}
                />
                ) : (
                  <div className="py-8 flex items-center justify-center">
                    <p style={{ color: C.textDim }}>Preparando teclado…</p>
                  </div>
                )
              ) : (
              <div className="relative flex-1 min-h-[min(42vh,320px)]">
                <div
                  className="min-h-[200px]"
                  style={{ paddingTop: DEFAULT_SONG_VIEW_HIGHWAY_TOP_PADDING_PX }}
                >
                  {heavySurfaceReady ? (
                  <SongFretboardFlow
                    _ref={highwayScrollRef}
                    notes={preparedSong.notes}
                    totalWidth={preparedSong.totalWidth}
                    divisions={preparedSong.divisions}
                    timeSignature={preparedSong.timeSignature}
                    activeStartTime={activeStartTime}
                    currentNoteIndex={currentNoteIndex}
                    pixelsPerBeat={pixelsPerBeat}
                    darkMode
                    measureInfos={preparedSong.measures}
                    staffFirstNoteX={staffFirstNoteX}
                    staffTimeGeometry={staffTimeGeometry}
                    scrollAnchorStartTime={scrollAnchorStartTime}
                    highwayContentAnchorX={highwayContentAnchorX}
                    notationLeadPx={staffNotationLeadPx ?? undefined}
                    staffScrollContentWidth={staffScrollContentWidth ?? undefined}
                    hidePlayhead
                  />
                  ) : (
                    <div className="py-8 flex items-center justify-center">
                      <p style={{ color: C.textDim }}>Preparando mástil…</p>
                    </div>
                  )}
                </div>
              </div>
              )
            ) : (
              <div className="py-16 flex items-center justify-center">
                <p style={{ color: C.textDim }}>{songParsePending ? "Cargando partitura…" : "Sin partitura"}</p>
              </div>
            )}
            </div>
              </div>
            </div>
          </div>

        </SongLearnCanvasBackdrop>

        {surface !== "keyboard" && (
        <div className="pointer-events-none fixed inset-x-0 bottom-6 z-50 flex justify-center px-2 sm:px-4">
          <div className="pointer-events-auto flex w-full max-w-[min(96vw,920px)] flex-row flex-wrap items-end justify-center gap-3">
            <div className="min-w-0 flex-1 flex justify-center">
              <TransportBar
              isPlaying={isPlaying}
              progressPercent={playbackProgressPercent}
              eventIndex={playbackEventIndex}
              totalEvents={playbackTimeline?.segments.length ?? 0}
              bpm={bpm}
              onPlayPause={handlePlayPause}
              onStop={handleStop}
              onBpmChange={setBpm}
              currentNote={currentNote}
            />
            </div>
            {showVideo && song.youtubeUrl && (
              <div
                className="shrink-0 w-[min(42vw,240px)] rounded-xl overflow-hidden border shadow-lg"
                style={{
                  backgroundColor: C.bgElevated,
                  borderColor: C.border,
                  boxShadow: "0 12px 40px rgba(0,0,0,0.45)",
                }}
              >
                <SongYouTubePlayer
                  ref={ytPlayerRef}
                  url={song.youtubeUrl}
                  onStateChange={handleYtStateChange}
                  className="border-0 rounded-none"
                />
              </div>
            )}
          </div>
        </div>
        )}

        <AnimatePresence>
          {learnCompleteOpen && (
            <motion.div
              className="fixed inset-0 z-[80] flex items-center justify-center bg-black/60 px-4 backdrop-blur-sm"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
            >
              <motion.div
                initial={{ opacity: 0, y: 22, scale: 0.94 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: 14, scale: 0.96 }}
                transition={{ type: "spring", stiffness: 360, damping: 28 }}
                className="w-full max-w-md rounded-3xl p-6 text-center shadow-2xl"
                style={{
                  backgroundColor: C.bgPanel,
                  border: `1px solid ${C.borderHover}`,
                  color: C.textMain,
                }}
              >
                <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-emerald-400/15 text-3xl">
                  ✓
                </div>
                <h2 className="font-serif text-3xl font-black">¡Canción aprendida!</h2>
                <p className="mt-2 text-sm" style={{ color: C.textMuted }}>
                  Buen trabajo. Ya puedes repetirla, pasar al modo practicar o volver a tu biblioteca.
                </p>
                <div className="mt-6 grid gap-3">
                  <button
                    onClick={restartLearn}
                    className="rounded-2xl px-4 py-3 text-sm font-bold text-white transition-transform hover:scale-[1.02]"
                    style={{ backgroundColor: C.accent }}
                  >
                    Intentarlo de nuevo
                  </button>
                  <button
                    onClick={startPracticeFromComplete}
                    className="rounded-2xl border px-4 py-3 text-sm font-bold transition-colors hover:bg-white/5"
                    style={{ borderColor: C.borderHover, color: C.textMain }}
                  >
                    Comenzar a practicar
                  </button>
                  <button
                    onClick={() => navigate(pathMode ? pathExit : "/songs")}
                    className="rounded-2xl px-4 py-3 text-sm font-semibold"
                    style={{ color: C.textMuted }}
                  >
                    {pathMode ? "Volver al camino" : "Ir a la biblioteca"}
                  </button>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        <AnimatePresence>
          {practiceCompleteOpen && practiceCompleteSummary && (
            <motion.div
              className="fixed inset-0 z-[80] flex items-center justify-center bg-black/60 px-4 backdrop-blur-sm"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
            >
              <motion.div
                initial={{ opacity: 0, y: 22, scale: 0.94 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: 14, scale: 0.96 }}
                transition={{ type: "spring", stiffness: 360, damping: 28 }}
                className="w-full max-w-md rounded-3xl p-6 text-center shadow-2xl"
                style={{
                  backgroundColor: C.bgPanel,
                  border: `1px solid ${C.borderHover}`,
                  color: C.textMain,
                }}
              >
                <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-amber-300/15 text-3xl">
                  {practiceScoreSummaryLines(practiceCompleteSummary).headline}
                </div>
                <h2 className="font-serif text-3xl font-black">Práctica completada</h2>
                <p className="mt-2 text-sm" style={{ color: C.textMuted }}>
                  {practiceScoreSummaryLines(practiceCompleteSummary).detail}
                </p>
                {!practiceScoringEnabled && (
                  <p className="mt-2 text-xs rounded-lg px-3 py-2" style={{ background: "rgba(251,191,36,0.12)", color: C.textMuted }}>
                    Pieza muy densa para micrófono: la reproducción siguió la partitura pero la
                    calificación puede ser limitada. Prueba entrada por cable o modo Aprender.
                  </p>
                )}
                {taskSubmitMessage && (
                  <p className="mt-2 text-xs font-semibold" style={{ color: "#34d399" }}>
                    {taskSubmitMessage}
                  </p>
                )}
                <div className="mt-6 grid gap-3">
                  <button
                    onClick={restartPractice}
                    className="rounded-2xl px-4 py-3 text-sm font-bold text-white transition-transform hover:scale-[1.02]"
                    style={{ backgroundColor: C.accent }}
                  >
                    Intentarlo de nuevo
                  </button>
                  <button
                    onClick={reviewInLearnMode}
                    className="rounded-2xl border px-4 py-3 text-sm font-bold transition-colors hover:bg-white/5"
                    style={{ borderColor: C.borderHover, color: C.textMain }}
                  >
                    Repasar en aprender
                  </button>
                  <button
                    onClick={() => navigate(pathMode ? pathExit : "/songs")}
                    className="rounded-2xl px-4 py-3 text-sm font-semibold"
                    style={{ color: C.textMuted }}
                  >
                    {pathMode ? "Volver al camino" : "Ir a la biblioteca"}
                  </button>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        <SettingsDrawer
          open={showSettings}
          onClose={() => setShowSettings(false)}
          showStaff={showStaff}
          onShowStaffChange={setShowStaff}
          showVideo={showVideo}
          onShowVideoChange={setShowVideo}
          inputMode={inputMode}
          onInputModeChange={setInputMode}
          deviceId={deviceId}
          onDeviceIdChange={setDeviceId}
          audioInputs={audioInputs}
          onRefreshDevices={refreshAudioInputs}
        />
      </div>
  );
};

export default SongStudioPage;
